import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { catchError, map, take, tap } from 'rxjs/operators';

import { AppConfig, CMPS_APP_CONFIG } from '../../app.config';
import { mapRetrieve } from '../../common/rest.operators';
import { toCamel } from '../td-daily-huddle/td-daily-huddle.reports.component';

import {
    Period,
    RxrData,
    RxRMetric,
    RxRMetrics,
    RxRResult,
} from './rxr.reports.component.model';

@Injectable({
    providedIn: 'root',
})
export class RxRService {
    constructor(
        private readonly _http: HttpClient,
        @Inject(CMPS_APP_CONFIG) private readonly _appConfig: AppConfig,
    ) {}

    backReportData: RxrData[] = new Array<RxrData>();
    userNameFilterQuery: string = '';
    roleFilterQuery: string = '';
    superGroupFilterQuery: string = '';
    selectedUser: RxrData;
    selectedRole: RxrData;
    selectedDate: string;
    selectedPeriod: Period;
    selectedDateRange: string;
    selectedMetrics: RxRMetrics[];
    generatedDate: string;
    startDate: string;
    endDate: string;

    params: {
        userID: string;
        roleID: string;
        date: string;
        dateRange: string;
        dateRequest: string;
        requestUserID: string;
        metricList: string;
    };

    public filterTable(): Observable<RxrData[]> {
        const resp = new Observable<RxrData[]>((o) => {
            const userFilter = this.backReportData.filter((el) => {
                return (
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    el.userName
                        .toLowerCase()
                        .indexOf(this.userNameFilterQuery.toLowerCase()) > -1
                );
            });

            const roleFilter = userFilter.filter((el) => {
                return (
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    el.roleName
                        .toLowerCase()
                        .indexOf(this.roleFilterQuery.toLowerCase()) > -1
                );
            });

            const supergroupFilter = roleFilter.filter((el) => {
                return (
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    el.superGroupName
                        .toLowerCase()
                        .indexOf(this.superGroupFilterQuery.toLowerCase()) > -1
                );
            });

            supergroupFilter.forEach((el: RxrData) => {
                el.metrics.forEach((m: RxRMetric) => {
                    const targets = this.selectedMetrics.find((i) => {
                        return i.id === Number(m.metricID);
                    });
                    m.target = targets ? targets.data.value : '0';
                });
            });

            supergroupFilter.forEach((el: RxrData) => {
                while (el.metrics.length < this.selectedMetrics.length) {
                    const newMetric: RxRMetric = {
                        metricID: '0',
                        periodName: 'null',
                        actual: '-',
                        variance: '-',
                        target: '0',
                    };
                    el.metrics.push(newMetric);
                }

                while (el.metrics.length > this.selectedMetrics.length) {
                    el.metrics.pop();
                }
            });

            const sorted = supergroupFilter.sort((a, b) => {
                return String(a.userName).toLowerCase() >
                    String(b.userName).toLowerCase()
                    ? 1
                    : -1;
            });

            const selectedUserIncluded = sorted.filter((o) => {
                return Number(o.userId) === Number(this.selectedUser.userId);
            });

            if (selectedUserIncluded.length > 0) {
                const selectedUser = selectedUserIncluded[0];
                const index = sorted.indexOf(selectedUser);
                sorted.splice(index, 1);
                sorted.unshift(selectedUser);
            }
            o.next(sorted);
        });
        return resp;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private getParams(result: RxRResult) {
        this.params = result.params.split('@').reduce((output, current) => {
            const keyValue = current.split('=').map((v) => v.trim());
            const key = (
                keyValue[0].charAt(0).toLowerCase() + keyValue[0].slice(1) ||
                keyValue[0]
            ).toString();
            const value = keyValue[1];
            if (key) {
                output[key] = value.replace(/^,+|,+$/g, '');
            }
            return output;
        }, {}) as any;
        const periodList = JSON.parse(result.periods) as Period[];
        this.selectedPeriod = periodList.filter((s) => {
            return s.PeriodName === 'ACTUAL';
        })[0];

        this.selectedDate = this.params.date;
        this.generatedDate = new Date().toDateString();

        const backReportData = (JSON.parse(result.resultSet) as any[]).map(
            (r) => toCamel(r),
        ) as RxrData[];

        this.backReportData = backReportData.reduce<RxrData[]>(
            (output, current, index) => {
                const curr = current as any;
                const prev = output.find((i) => i.userId === current.userId);
                if (prev) {
                    prev.metrics.push({
                        metricID: curr.metricID,
                        periodName: curr.en,
                        actual: isNaN(curr.actual)
                            ? '-'
                            : Number(curr.actual).toFixed(2).toString(),
                        variance: isNaN(curr.variance)
                            ? '-'
                            : Number(curr.variance).toFixed(2).toString(),
                        target: '0',
                    });
                } else {
                    output.push({
                        userId: curr.userId,
                        userName: curr.userName,
                        superGroupId: null,
                        superGroupName: curr.superGroup,
                        roleId: null,
                        roleName: curr.role,
                        metrics: [
                            {
                                metricID: curr.metricID,
                                periodName: curr.en,
                                actual: isNaN(curr.actual)
                                    ? '-'
                                    : Number(curr.actual).toFixed(2).toString(),
                                variance: isNaN(curr.variance)
                                    ? '-'
                                    : Number(curr.variance)
                                          .toFixed(2)
                                          .toString(),
                                target: '0',
                            },
                        ],
                    });
                }

                output.forEach((element) => {
                    element.metrics = element.metrics.reduce(
                        (unique: RxRMetric[], o: RxRMetric) => {
                            if (
                                !unique.some(
                                    (obj) =>
                                        Number(obj.metricID) ===
                                        Number(o.metricID),
                                )
                            ) {
                                unique.push(o);
                            }
                            return unique;
                        },
                        [],
                    );
                });
                return JSON.parse(JSON.stringify(output));
            },
            [],
        );
        return forkJoin([this.getRoleName(), this.getUserName()]);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public getUserName() {
        return this.retrieveUsersNameById(this.params.userID).pipe(
            take(1),
            tap((r) => {
                if (r.length > 0) {
                    this.selectedUser = r[0];
                }
            }),
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public getRoleName() {
        return this.retrieveRolesNameById(this.params.roleID).pipe(
            take(1),
            tap((r) => {
                if (r.length > 0) {
                    this.selectedRole = r[0];
                }
            }),
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    public generateParameters(rxrResult: RxRResult) {
        return this.getParams(rxrResult);
    }

    retrieveRolesNameById(roleIds: string): Observable<RxrData[]> {
        const params = new HttpParams().set('roleIds', roleIds);
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GetRolesNameById`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }

    retrieveUsersNameById(userIds: string): Observable<RxrData[]> {
        const params = new HttpParams().set('userIds', userIds);
        return this._http
            .get(
                `${this._appConfig.API_ENDPOINTS.REPORT}/report/GetUsersNameById`,
                { params },
            )
            .pipe(
                mapRetrieve(),
                catchError((e) => {
                    console.error('error api', e);
                    // tslint:disable-next-line: deprecation
                    return of({});
                }),
            );
    }
}
